import React, {FC, useEffect, useMemo, useState, KeyboardEvent, ChangeEvent} from 'react'
import {
  Box,
  Grid,
  InputAdornment,
  SelectChangeEvent,
  TextField,
  Autocomplete,
} from '@mui/material'
import {Search} from '@mui/icons-material';
import {ExpandableSection} from '../common/ExpandableSection';
import SelectInput from '../SelectInput';
import {debounce} from 'throttle-debounce';
import {useIsMounted} from 'usehooks-ts';
import moment from 'moment'
import DatePicker from '../common/inputs/DatePicker/DatePicker';
import FilterInput from '../common/inputs/FilterInput';
import {
  AutocompleteOption,
  FilterBySelectProps,
  FilterInputProps,
  SelectInputProps,
  SortFilterProps
} from "./interfaces";

const styles = {
  icon: {
    color: 'rgba(0, 0, 0, 0.54)'
  }
};

const fieldType = {number: "number", text: "text"};

/**
 *
 * @param groupByOption
 * @param sortByOption
 * @param locations
 * @param filterByOption
 * @param defaultStartDate
 * @param defaultEndDate
 * @param defaultSortBy
 * @param defaultLocation
 * @param filterBySelectOption
 * @param disableFuture
 * @param debounceTime
 * @param firstDatePickerLabel
 * @param secondDatePickerLabel
 * @param sortDir
 * @param minDate
 * @param maxDate
 * @param filterCallback
 */
const SortFilter = ({
                      groupByOption,
                      sortByOption,
                      locations,
                      filterByOption,
                      defaultStartDate,
                      defaultEndDate,
                      defaultSortBy,
                      defaultLocationId,
                      filterBySelectOption,
                      disableFuture = undefined,
                      debounceTime = 500,
                      firstDatePickerLabel = 'Start Date',
                      secondDatePickerLabel = 'End Date',
                      sortDir = 'asc',
                      minDate = undefined,
                      maxDate = undefined,
                      filterCallback = () => {
                      },
                      expandable,
                      expanded,
                      onExpandedChange,
                      additionalFilters,
                    }: SortFilterProps) => {

  const [filters, setFilters] = useState<FilterInputProps[]>(filterByOption || []);
  const [selectFilters, setSelectFilter] = useState<FilterBySelectProps[]>([]);
  const [startDate, setStartDate] = useState<string>(defaultStartDate || "");
  const [endDate, setEndDate] = useState<string>(defaultEndDate || "");
  const [locationId, setLocationId] = useState<string>(defaultLocationId || "");
  const [orderDir, setOrderDir] = useState<string>(sortDir);
  const [groupBy, setGroupBy] = useState<string>(
    groupByOption ? groupByOption[0]?.value : ""
  );
  const [sortBy, setSortBy] = useState<string>(
    defaultSortBy ? defaultSortBy : sortByOption ? sortByOption[0]?.value : ""
  );

  const isMounted = useIsMounted()

  useEffect(() => {
    setLocationId(defaultLocationId || "");
  }, [defaultLocationId]);

  const locationOptions = useMemo<Array<AutocompleteOption>>(() => {
    if (locations?.length) {
      return locations.map(location => ({id: location.id, label: location.locationName}));
    }
    return [];
  }, [locations]);

  // Sentinel option (id: '') loads before real locations. Check for non-sentinel option
  // to preserve a URL-param locationId that hasn't matched real options yet.
  const hasRealLocationOptions = useMemo(
    () => locationOptions.some(opt => opt.id !== ''),
    [locationOptions]
  );

  useEffect(() => {
    if (hasRealLocationOptions && locationId && !locationOptions.find(opt => opt.id === locationId)) {
      setLocationId(locationOptions[0].id);
    }
  }, [locationOptions, locationId, hasRealLocationOptions]);

  const onChangeCallBack = (
    e: SelectChangeEvent,
    setValue: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const value = e.target.value;
    setValue(value);
  };

  const changeState = (value: any, setState: (value: any) => void) => {
    setState(value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement;
    const {key} = event;
    if (
      target.type === fieldType.number &&
      ["e", "E", "+", "-"].includes(key)
    ) {
      event.preventDefault();
    }
  }

  const handleChangeSearchFilter = (
    e: ChangeEvent<HTMLInputElement>,
    option: FilterInputProps
  ) => {
    const {name, value, type} = e.target;
    let filteredValue = value;

    if (option.numberField) {
      filteredValue = value.replace(/\D/g, ""); // Remove non-numeric characters
    }

    if (type === fieldType.number) {
      // Remove non-numeric characters
      e.target.value = value.replace(/\D/g, "");
    }
    const newFilter = {...option, value: filteredValue};
    if (option.inputProps) newFilter.inputProps = option.inputProps;

    // Copy local filters state
    const localFilters = [...filters];
    let filterToReplace;

    if (localFilters?.length) {
      filterToReplace = localFilters.find(
        (item: FilterInputProps) => item.name === name
      );
    }

    if (filterToReplace) {
      Object.assign(filterToReplace, newFilter);
    } else {
      localFilters.push({
        ...newFilter,
        label: e.target.labels && e.target.labels[0] ? e.target.labels[0].innerText : ''
      });
    }

    changeState(localFilters, setFilters);
  };

  const findValueByName = (name: string): string => {
    if (filters.length) {
      return filters.find((item) => item.name === name)?.value || '';
    }
    return '';
  };

  const handleChangeSelectFilter = (filter: FilterBySelectProps) => {

    const newFilter = filter;
    const localFilters = [...selectFilters];
    let filterToReplace;

    if (localFilters?.length) {
      filterToReplace = localFilters.find(
        (item: { label: string; value: string, name: string }) => item.name === filter.name
      );
    }

    if (filterToReplace) {
      Object.assign(filterToReplace, newFilter);
    } else {
      localFilters.push(newFilter);
    }

    changeState(localFilters, setSelectFilter);
  };

  const handleDateChange = (date: any, setState: any) => {
    if (date) {
      if (setState === setEndDate && startDate && moment(date).isBefore(startDate)) {
        const newStartDate = moment(date).add(-1, 'months');
        changeState(newStartDate.toISOString(), setStartDate);
      }
      changeState(date.toISOString(), setState);
    } else if (setState === setStartDate) {
      // When clearing start date, set it to 1 month before end date (if end date is available)
      if (endDate) {
        const newStartDate = moment(endDate).add(-1, 'months');
        changeState(newStartDate.toISOString(), setStartDate);
      } else {
        // No end date to base the calculation on; just clear the start date
        changeState(null, setStartDate);
      }
    } else if (setState === setEndDate) {
      // When clearing end date, set it to 1 month after start date (if start date is available)
      if (startDate) {
        const newEndDate = moment(startDate).add(1, 'months');
        changeState(newEndDate.toISOString(), setEndDate);
      } else {
        // No start date to base the calculation on; just clear the end date
        changeState(null, setEndDate);
      }
    }
  };

  const findValueByNameSelect = (name: string) => {
    if (selectFilters) {
      return selectFilters.find((item) => item.name === name)?.value;
    }
  };

  useEffect(() => {
    const debounced = debounce(Number.isNaN(debounceTime) ? 500 : debounceTime, filterCallback);
    if (isMounted()) {
      debounced({
        filters,
        selectFilters,
        groupBy,
        sortBy,
        startDate,
        endDate,
        location: locations?.find((option) => option.id === locationId),
        sortDir: orderDir,
      });
    }
    return debounced.cancel;
  }, [filters, selectFilters, groupBy, sortBy, startDate, endDate, locations, locationId, filterCallback, orderDir, debounceTime, isMounted]);

  const FilterSelectInput: FC<{ label: string, name: string, option: SelectInputProps[], hasAction?: boolean, }>
    = ({label, name, option, hasAction}) => {
    const valueByName: string | undefined = findValueByNameSelect(name) || option[0].value as string;
    return (
      <SelectInput
        label={label}
        id={label}
        selectData={option}
        value={valueByName}
        hasAction={hasAction}
        shrinkLabel={valueByName === '' || !!valueByName}
        onChange={(e) => handleChangeSelectFilter({
          value: e.target.value,
          name: name,
          label: label,
          option: option
        })}
      />
    )
  };

  const secondaryFilterContent = (
    <>
      {
        filters?.length === 1 && !groupByOption ? (
          filterBySelectOption && filterBySelectOption.slice(1)?.map((option, index) => (
            <Grid item xs={12} sm={index === 0 ? 6 : index === 3 ? 6 : 3} key={option.label}>
              <FilterSelectInput hasAction={false} label={option.label} name={option.name}
                                 option={option.option}/>
            </Grid>
          ))
        ) : filterBySelectOption && filterBySelectOption?.map((option, index) => (
          <Grid item xs={12} sm={index === 0 ? 6 : index === 3 ? 6 : 3} key={option.label}>
            <FilterSelectInput hasAction={false} label={option.label} name={option.name}
                               option={option.option}/>
          </Grid>
        ))
      }
      {
        filters && filters?.slice(2).map((option) => (
          <Grid item xs={12} sm={filterBySelectOption && filterBySelectOption?.length > 1 ? 6 : 3}
                key={option.name}>
            <FilterInput
              type={(option.type || "text") as 'text' | 'number'}
              name={option.name}
              value={findValueByName(option.name)}
              onKeyDown={handleKeyDown}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeSearchFilter(event, option)}
              placeholder={`Filter results by ${option.label.toLowerCase()}`}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search fontSize='medium' sx={styles.icon}/>
                  </InputAdornment>
                ),
                ...option.inputProps
              }}
              fullWidth
              label={option.label}
              required={option.required}
              disabled={option.disabled}
            />
          </Grid>
        ))
      }
    </>
  );

  return (
    <Box>
      <Grid container spacing={2}>
        {locationOptions?.length > 0 && <Grid item xs={12} sm>
          <Autocomplete
            id='location'
            value={locationOptions.find(option => option.id === locationId) || locationOptions[0] || null}
            disablePortal
            options={locationOptions}
            disableClearable
            onChange={(_event, newValue) => setLocationId(newValue.id ?? "")}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            renderInput={(params) => <TextField {...params} label="Location" variant="standard"/>}
          />
        </Grid>
        }
        {
          defaultStartDate && <Grid item xs={12} sm>
            <DatePicker
              value={startDate}
              onChange={(e) => handleDateChange(e, setStartDate)}
              label={firstDatePickerLabel}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Grid>
        }
        {
          defaultEndDate && <Grid item xs={12} sm>
            <DatePicker
              value={endDate}
              onChange={(e) => handleDateChange(e, setEndDate)}
              label={secondDatePickerLabel}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Grid>
        }
      </Grid>
      {additionalFilters &&
        <Grid container mt={0.5} spacing={2}>
          {additionalFilters}
        </Grid>
      }
      {
        sortByOption ?
          <Grid container mt={0.5} spacing={2} sx={{
            '& .MuiFormControl-root.MuiFormControl-fullWidth': {
              mt: 0,
              mb: 0
            }
          }}>
            {
              filters && filters?.slice(0, 2).map((option, index) => (
                <Grid item xs={12} sm={index === 0 ? 6 : 3} key={option.name}>
                  <FilterInput
                    type={(option.type || "text") as 'text' | 'number'}
                    name={option.name}
                    value={findValueByName(option.name)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeSearchFilter(event, option)}
                    placeholder={`Filter results by ${option.label.toLowerCase()}`}
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Search fontSize='medium' sx={styles.icon}/>
                        </InputAdornment>
                      ),
                      ...option.inputProps
                    }}
                    label={option.label}
                    required={option.required}
                    disabled={option.disabled}
                  />
                </Grid>
              ))
            }

            {
              filters && filters?.length === 1 && !groupByOption && (
                filterBySelectOption && filterBySelectOption.slice(0, 1)?.map((option) => (
                  <Grid item xs={12} sm={3} key={option.label}>
                    <FilterSelectInput label={option.label} name={option.name} option={option.option} hasAction={false}/>
                  </Grid>
                ))
              )
            }

            {groupByOption &&
              <Grid item xs={12} sm={3}>
                <SelectInput
                  label='Group By'
                  id='group-by'
                  selectData={groupByOption}
                  value={groupBy}
                  hasAction={true}
                  onChange={(e) => onChangeCallBack(e, setGroupBy)}
                  onNextClick={() => setOrderDir('asc')}
                  onPrevClick={() => setOrderDir('desc')}
                  activeState={orderDir === 'asc'}
                  handleToggle={() => setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')}
                />
              </Grid>
            }
            <Grid item xs={12} sm={3}>
              <SelectInput
                label='Sort By'
                id='sort-by'
                selectData={sortByOption}
                value={sortBy}
                hasAction={true}
                onChange={(e) => onChangeCallBack(e, setSortBy)}
                onNextClick={() => setOrderDir('asc')}
                onPrevClick={() => setOrderDir('desc')}
                activeState={orderDir === 'asc'}
                handleToggle={() => setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')}
              />
            </Grid>

            {expandable ?
              <Grid item xs={12}>
                <ExpandableSection
                  expanded={expanded}
                  onExpandedChange={onExpandedChange}
                  expandLabel="Show more filters"
                  collapseLabel="Show fewer filters"
                >
                  <Grid container spacing={2} sx={{mt: 0}}>
                    {secondaryFilterContent}
                  </Grid>
                </ExpandableSection>
              </Grid>
              :
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {secondaryFilterContent}
                </Grid>
              </Grid>
            }
          </Grid>
          : ""
      }
    </Box>
  )
}


export default SortFilter;
